import React, {useContext, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {Button} from "rsuite";
import cs from "classnames";
import Help from "./help";
import CalculationContext from "../context";
import {useField} from "../store/fields-slice";
import PriceDisplay from "./price-display";
import DebouncedInput from "./debounced-input";
import {makeId, selectAllChoicesPrices, setChoicePriceField, useChoicePrice} from "../store/choices-prices-slice";
import {useColSpan, useColumnCount, useColumnKeys, useHideOccupancyChoices} from "../store/state-slice";
import {currencies} from "../../../currency/model/currencies.model";

import {
    setChoiceField,
    useChoice,
    useChoiceColumns,
    useChoiceLabel,
    useChoiceSettings,
    useMarginFractionTitle,
    useOverrideColumns,
} from "../store/choices-slice";
import {useOrganizationQuery} from "../../../store/api";

const purchaseSelector = ({purchase}) => purchase
const retailSelector = ({retail}) => retail
const marginSelector = ({margin}) => margin
const memberDiscountSelector = ({memberDiscount}) => memberDiscount

const getCurrencyFactor = (currency) => {
    const digits = currencies?.[currency]?.decimal_digits ?? 2;
    return Math.pow(10, digits);
};

const parseNumber = (value) => {
    if (null == value) {
        return null;
    }

    if ('string' === typeof value) {
        value = value.trim();
        if ('' === value) {
            return null;
        }
    }

    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const basePriceToMinor = (value, currency) => {
    const num = parseNumber(value);
    if (null === num) {
        return null;
    }

    return Math.round(num * getCurrencyFactor(currency));
};

const roundToCurrencyMajor = (value, currency) => {
    const num = parseNumber(value);
    if (null === num) {
        return null;
    }

    const rounded = Math.round(num * getCurrencyFactor(currency)) / getCurrencyFactor(currency);

    return rounded;
};

const makeChoicePriceId = (choiceId, groupId, paxId) => undefined === paxId ?
    choiceId + "|" + groupId :
    choiceId + "|" + groupId + "|" + paxId;

export default function CalculationTableFooterPrices({choiceId, view, baselineChoiceId}) {
    const {
        hideCalculatedPurchasePrice,
        showSingleRoomUpcharge,
        showPreviousPriceField,
        activateTotalMargin,
    } = useChoiceSettings()

    const hideOccupancyChoices = useHideOccupancyChoices()
    const colSpan = useColSpan()
    const choiceColumns = useChoiceColumns()
    const columnCount = useColumnCount() + (hideOccupancyChoices ? -1 : choiceColumns)
    const columnKeys = useColumnKeys()
    const choicesPrices = useSelector(selectAllChoicesPrices)
    const choiceLabel = useChoiceLabel(choiceId)
    const [show, setShow] = useState(true)
    const rows = []
    const {organization} = useContext(CalculationContext)
    const {data: organizationData} = useOrganizationQuery(organization)
    const currency = useField('currency')

    const settings = organizationData?.settings?.tourismJourneyCalculationSettings ?? null
    const subsidyPotSettings = organizationData?.settings?.tourismSubsidyPotSettings ?? null

    const alignment = settings?.alignment ?? 'left'
    const {
        isDisplaySubsidyPotInCalculation,
    } = subsidyPotSettings ?? {}

    const baseline = baselineChoiceId ? useChoice(baselineChoiceId) : null;

    const baselineByKey = React.useMemo(() => {
        const m = new Map();
        baseline?.columns?.forEach((c) => m.set(c.key, c));
        return m;
    }, [baseline]);

    const upchargeBasePriceByKey = React.useMemo(() => {
        const map = new Map();

        if (!baselineChoiceId || 'upcharge' !== view) {
            return map;
        }

        const baseByKey = new Map();
        const candidates = [];

        for (const {key, groupId, paxId} of columnKeys) {
            const directId = makeChoicePriceId(baselineChoiceId, groupId, paxId);
            const groupOnlyId = makeChoicePriceId(baselineChoiceId, groupId);
            const directRow = choicesPrices.find(({id}) => id === directId);
            const groupRow = choicesPrices.find(({id}) => id === groupOnlyId);
            const base =
                directRow?.upchargeGroupBasePrice
                ?? directRow?.upchargeGroupBasePriceDefault
                ?? groupRow?.upchargeGroupBasePrice
                ?? groupRow?.upchargeGroupBasePriceDefault;
            const baseNumber = basePriceToMinor(base, currency);

            baseByKey.set(key, baseNumber);

            if (Number.isFinite(baseNumber)) {
                candidates.push(baseNumber);
            }
        }

        const nonZeroCandidates = candidates.filter((c) => 0 !== c);
        const sharedBaseMinor = nonZeroCandidates[0] ?? candidates[0] ?? null;

        for (const {key} of columnKeys) {
            let baseNumber = baseByKey.get(key);

            if (
                (!Number.isFinite(baseNumber) || null === baseNumber) ||
                (0 === baseNumber && Number.isFinite(sharedBaseMinor) && 0 !== sharedBaseMinor)
            ) {
                baseNumber = sharedBaseMinor;
            }

            if (!key || null === baseNumber || !Number.isFinite(baseNumber)) {
                continue;
            }

            map.set(key, baseNumber);
        }

        return map;
    }, [baselineChoiceId, choicesPrices, columnKeys, view, currency]);

    const baseRetailSplit = React.useCallback(
        (cell) => {
            const base = upchargeBasePriceByKey.has(cell.key) ? upchargeBasePriceByKey.get(cell.key) : null;
            const baseNum = parseNumber(base);

            if (null === baseNum) {
                return null;
            }

            const currentRaw = cell?.overrideRetail ?? cell?.retail;
            const current = parseNumber(currentRaw);

            if (
                null == currentRaw ||
                '' === currentRaw ||
                null === current ||
                (0 === current && 0 !== baseNum)
            ) {
                return {base: baseNum, upcharge: 0};
            }

            return {base: baseNum, upcharge: current - baseNum};
        },
        [upchargeBasePriceByKey]
    );

    const purchaseDeltaSplit = React.useCallback(
        (cell) => {
            const baseCell = baselineByKey.get(cell.key);
            if (!baseCell) return null;

            const base = parseNumber(baseCell?.purchase);
            const cur = parseNumber(cell?.purchase);

            if (null === base || null === cur) {
                return null;
            }

            return {base, upcharge: cur - base};
        },
        [baselineByKey]
    );

    const retailDeltaSplit = React.useCallback(
        (cell) => {
            if (!upchargeBasePriceByKey.has(cell.key)) {
                return null;
            }

            const basePrice = parseNumber(upchargeBasePriceByKey.get(cell.key));
            const totalPriceRaw = cell?.overrideRetail ?? cell?.retail;
            const totalPrice = parseNumber(totalPriceRaw);

            const baseCellSameColumn = baselineByKey.get(cell.key);
            const baseTotalPriceRaw = baseCellSameColumn?.overrideRetail ?? baseCellSameColumn?.retail;
            const baseTotalPrice = parseNumber(baseTotalPriceRaw);

            if (null === basePrice || null === baseTotalPrice || null === totalPrice) {
                return null;
            }

            const season = baseTotalPrice - basePrice;
            const room = totalPrice - baseTotalPrice;
            return {base: basePrice, upcharge: season + room, parts: [season, room]};
        },
        [upchargeBasePriceByKey, baselineByKey]
    );

    if ('total' === view) {
        rows.push(
            <DisplayRow
                key="purchaseSum"
                title="EK gesamt"
                help="tourism-journey-calculation.purchase-price-sum-help"
                choiceId={choiceId}
                amountSelector={purchaseSelector}
            />
        );
        if (!hideCalculatedPurchasePrice) {
            rows.push(
                <DisplayRow
                    key="retailSum"
                    title="VK gesamt"
                    help="tourism-journey-calculation.retail-price-sum-help"
                    choiceId={choiceId}
                    amountSelector={retailSelector}
                />
            )
        }
    } else if ('upcharge' === view) {
        if (choiceId === baselineChoiceId) {
            rows.push(
                <DisplayRow
                    key="purchaseSumBaseline"
                    title="EK gesamt (Basis)"
                    help="tourism-journey-calculation.purchase-price-sum-help"
                    choiceId={choiceId}
                    amountSelector={purchaseSelector}
                />
            );
            if (!hideCalculatedPurchasePrice) {
                rows.push(
                    <DisplayRow
                        key="retailSumBaseline"
                        title="VK gesamt (Basis)"
                        help="tourism-journey-calculation.retail-price-sum-help"
                        choiceId={choiceId}
                        splitAmountSelector={baseRetailSplit}
                    />
                );
            }
        } else {
            rows.push(
                <DisplayRow
                    key="purchaseSumUpcharge"
                    title="EK gesamt (Basis + Aufpreis)"
                    help="tourism-journey-calculation.purchase-price-sum-help"
                    choiceId={choiceId}
                    splitAmountSelector={purchaseDeltaSplit}
                />
            );
            if (!hideCalculatedPurchasePrice) {
                rows.push(
                    <DisplayRow
                        key="retailSumUpcharge"
                        title="VK gesamt (Basis + Aufpreis)"
                        help="tourism-journey-calculation.retail-price-sum-help"
                        choiceId={choiceId}
                        splitAmountSelector={retailDeltaSplit}
                    />
                )
            }
        }
    }

    if (!hideCalculatedPurchasePrice) {
        rows.push(
            <DisplayRow
                key="factors"
                title="Marge / DB"
                help="tourism-journey-calculation.margin-help"
                choiceId={choiceId}
                percentageSelector={marginSelector}
                color
            />
        )
    }

    rows.push(
        <InputRow
            key="retailPrices"
            title={hideCalculatedPurchasePrice ? "VK" : "VK überschreiben"}
            help="tourism-journey-calculation.overwrite-help"
            field="retailPrice"
            choiceId={choiceId}
            view={view}
            baselineChoiceId={baselineChoiceId}
        />
    )

    if (true === isDisplaySubsidyPotInCalculation) {
        rows.push(
            <InputRow
                key="subsidyDiscount"
                title="tourism-subsidy-pot.journey-calculation.field.subsidy-discount"
                field="subsidyDiscount"
                choiceId={choiceId}
            />
        )

        rows.push(
            <DisplayRow
                key="memberDiscount"
                title="tourism-subsidy-pot.journey-calculation.field.member-discount"
                field="memberDiscount"
                choiceId={choiceId}
                amountSelector={memberDiscountSelector}
            />
        )

        rows.push(
            <InputRow
                key="memberPrice"
                title="tourism-subsidy-pot.journey-calculation.field.member-price"
                field="memberPrice"
                choiceId={choiceId}
            />
        )
    }

    rows.push(
        <InputRow
            key="manualFactors"
            title="DB überschreiben"
            field="manualFactor"
            choiceId={choiceId}
        />
    )

    rows.push(
        <MarginFractionRows key="marginFractions" choiceId={choiceId}/>
    )

    if (activateTotalMargin) {
        rows.push(
            <MarginTotalRows key="marginTotalRows" choiceId={choiceId}/>
        )
    }

    if (showSingleRoomUpcharge) {
        rows.push(
            <SingleRoomUpcharge
                key="singleRoomUpcharge"
                choiceId={choiceId}
                basePriceByKey={upchargeBasePriceByKey}
                baselineByKey={baselineByKey}
            />
        )
    }

    if (showPreviousPriceField) {
        rows.push(
            <PreviousPricesRow key="previousPrices" choiceId={choiceId}/>
        )
    }

    return (
        <>
            {"right" === alignment ? (
                <tr className="group">
                    <th colSpan={colSpan + columnCount} style={{textAlign: alignment}}>
                        {choiceLabel}
                        <Button appearance="link" size="xs" onClick={() => setShow(!show)} style={{color: "black"}}>
                            <span className={"fa fa-" + (show ? "minus" : "plus")}/>
                        </Button>
                    </th>
                </tr>
            ) : (
                <tr className="group">
                    <th>
                        <Button appearance="link" size="xs" onClick={() => setShow(!show)} style={{color: "black"}}>
                            <span className={"fa fa-" + (show ? "minus" : "plus")}/>
                        </Button>
                    </th>
                    <th colSpan={colSpan} className="sticky">
                        {choiceLabel}
                    </th>
                    <th colSpan={columnCount}/>
                </tr>
            )}
            {show && rows}
        </>
    )
}

function FooterRow({title, help, children}) {
    const {i18n} = useContext(CalculationContext)
    const {colSpan} = useChoiceSettings()
    const choiceColumns = useChoiceColumns()
    const hideOccupancyChoices = useHideOccupancyChoices()

    return (
        <tr>
            <th className="text-right border-right" colSpan={colSpan}>
                {title.includes(" ") ? title : i18n.tr(title)}
                {help && (
                    <>
                        {" "}
                        <Help>{i18n.tr(help)}</Help>
                    </>
                )}
            </th>
            {!hideOccupancyChoices && <td className="border-right" colSpan={choiceColumns}/>}
            {children}
        </tr>
    )
}

function DisplayRow({
    choiceId,
    color,
    amountSelector,
    percentageSelector,
    splitAmountSelector,
    ...props
}) {
    const {currencyConverter, percentageConverter} = useContext(
        CalculationContext
    );

    const currency = useField('currency');
    const marginParticipants = useField('marginParticipants');
    const {updating, columns} = useChoice(choiceId);

    return (
        <FooterRow {...props}>
            {columns.map((cell) => {
                const {key, from, to, border} = cell;

                const splitRaw = splitAmountSelector ? splitAmountSelector(cell) ?? {
                    base: cell.retail ?? 0,
                    upcharge: 0,
                    parts: null,
                } : null;

                const split = splitRaw
                    ? {
                        ...splitRaw,
                        base: Number.isFinite(Number(splitRaw.base))
                            ? Number(splitRaw.base)
                            : 0,
                        upcharge: Number.isFinite(Number(splitRaw.upcharge))
                            ? Number(splitRaw.upcharge)
                            : 0,
                        parts: Array.isArray(splitRaw.parts)
                            ? splitRaw.parts
                                .map((part) => Number(part))
                                .filter(Number.isFinite)
                            : null,
                    } : null;

                const hasSplit = split && Number.isFinite(split.base) && Number.isFinite(split.upcharge);

                const hasParts =
                    hasSplit &&
                    Array.isArray(split.parts) &&
                    0 < split.parts.length &&
                    split.parts.every((p) => Number.isFinite(Number(p)));

                const parts = hasParts ? split.parts.map(Number) : null;

                const amount = !split && amountSelector ? amountSelector(cell) : null;
                const percentage = percentageSelector ? percentageSelector(cell) : null;

                const isNum = 'number' === typeof amount && isFinite(amount);
                const isPct = 'number' === typeof percentage && isFinite(percentage);

                const sign = hasSplit && 0 > split.upcharge ? "−" : "+";
                const absUp = hasSplit ? Math.abs(split.upcharge) : null;

                return (
                    <td
                        key={key}
                        className={cs("text-right", "pax", cell.color, {
                            "border-right": border,
                            "margin-pax": from <= marginParticipants && marginParticipants <= to,
                            "margin-tax": isPct && color && cell.color,
                            [cell.color]: isPct && color && cell.color,
                        })}
                    >
                        {hasSplit ? (
                            hasParts ? (
                                <div className="split-amount">
                                    <PriceDisplay
                                        number
                                        money={{amount: split.base, currency}}
                                        currencyConverter={currencyConverter}
                                        updating={updating}
                                    />
                                    {parts
                                        .filter((p) => p !== 0)
                                        .map((p, idx) => (
                                            <>
                                                <br/>
                                                {" "}{p < 0 ? "−" : "+"}{" "}
                                                <span
                                                    className={cs({
                                                        "delta-neg": p < 0,
                                                        "delta-pos": p > 0,
                                                    })}
                                                >
                                                    <PriceDisplay
                                                        number
                                                        money={{amount: Math.abs(p), currency}}
                                                        currencyConverter={currencyConverter}
                                                        updating={updating}
                                                    />
                                                </span>
                                            </>
                                        ))}
                                    {isPct && (
                                        <em>
                                            <br/>
                                            {percentageConverter.toView(percentage)}
                                        </em>
                                    )}
                                </div>
                            ) : !Number.isFinite(split.base) || 0 === split.base ? (
                                <PriceDisplay
                                    number
                                    money={{amount: split.base + split.upcharge, currency}}
                                    currencyConverter={currencyConverter}
                                    updating={updating}
                                />
                            ) : 0 === absUp ? (
                                <PriceDisplay
                                    number
                                    money={{amount: split.base, currency}}
                                    currencyConverter={currencyConverter}
                                    updating={updating}
                                />
                            ) : (
                                <div className="split-amount">
                                    <PriceDisplay
                                        number
                                        money={{amount: split.base, currency}}
                                        currencyConverter={currencyConverter}
                                        updating={updating}
                                    />
                                    {split.upcharge && 0 !== split.upcharge ? (
                                        <>
                                            <br/>
                                            {" " + sign + " "}
                                            <span
                                                className={cs({
                                                    "delta-neg": 0 > split.upcharge,
                                                    "delta-pos": 0 < split.upcharge,
                                                })}
                                            >
                                              <PriceDisplay
                                                  number
                                                  money={{amount: absUp, currency}}
                                                  currencyConverter={currencyConverter}
                                                  updating={updating}
                                              />
                                            </span>
                                        </>
                                    ) : null}
                                    {isPct && (
                                        <em>
                                            <br/>
                                            {percentageConverter.toView(percentage)}
                                        </em>
                                    )}
                                </div>
                            )
                        ) : amountSelector || Number.isFinite(amount) ? (
                            <>
                                {isNum && (
                                    <PriceDisplay
                                        number
                                        money={{amount, currency}}
                                        currencyConverter={currencyConverter}
                                        updating={updating}
                                    />
                                )}
                                {isPct && (
                                    <em>
                                        <br/>
                                        {percentageConverter.toView(percentage)}
                                    </em>
                                )}
                            </>
                        ) : isPct ? (
                            percentageConverter.toView(percentage)
                        ) : null}
                    </td>
                );
            })}
        </FooterRow>
    );
}

export function BasePriceRow({choiceId}) {
    const dispatch = useDispatch()
    const columnKeys = useColumnKeys()
    const choicesPrices = useSelector(selectAllChoicesPrices)
    const {i18n} = useContext(CalculationContext)
    const {updating} = useChoice(choiceId)
    const firstColumn = columnKeys?.[0]
    const choicePrice = useChoicePrice(choiceId, firstColumn?.groupId, firstColumn?.paxId)
    const choiceGroupPrice = useChoicePrice(choiceId, firstColumn?.groupId)
    const {colSpan} = useChoiceSettings()
    const hideOccupancyChoices = useHideOccupancyChoices()
    const choiceColumns = useChoiceColumns()

    if (!firstColumn) {
        return null
    }

    const basePrice =
        choicePrice?.upchargeGroupBasePrice
        ?? choicePrice?.upchargeGroupBasePriceDefault
        ?? choiceGroupPrice?.upchargeGroupBasePrice
        ?? choiceGroupPrice?.upchargeGroupBasePriceDefault
        ?? ''

    return (
        <tr>
            <th className="text-right border-right" colSpan={colSpan}>
                {i18n.tr("tourism-journey-calculation.upcharge-group-base-price.title")}{" "}
                <Help>{i18n.tr("tourism-journey-calculation.upcharge-group-base-price.help")}</Help>
            </th>
            {!hideOccupancyChoices && <td className="border-right" colSpan={choiceColumns}/>}
            <td colSpan={columnKeys.length} className="text-right pax">
                <DebouncedInput
                    throttle={2000}
                    type="number"
                    step="0.01"
                    style={{maxWidth: "100%"}}
                    value={basePrice ?? ""}
                    disabled={updating}
                    onChange={onChange}
                />
            </td>
        </tr>
    )

    function onChange(value) {
        value = 'string' === typeof value ? value.trim() : value;

        const touched = new Set()

        for (const {groupId, paxId} of columnKeys) {
            const directId = makeChoicePriceId(choiceId, groupId, paxId)
            const groupOnlyId = makeChoicePriceId(choiceId, groupId)
            const hasDirect = choicesPrices.some(({id}) => id === directId)
            const targetPax = hasDirect ? paxId : undefined
            const targetId = hasDirect ? directId : groupOnlyId

            if (touched.has(targetId)) {
                continue
            }

            touched.add(targetId)

            dispatch(setChoicePriceField("upchargeGroupBasePrice", choiceId, groupId, targetPax, value ?? ""))
        }
    }
}

function InputRow({choiceId, field, view, baselineChoiceId, ...props}) {
    const overrideColumns = useOverrideColumns()
    const marginParticipants = useField("marginParticipants")
    const isShowUpchargeDelta = view === 'upcharge' && field === 'retailPrice'

    return (
        <FooterRow {...props}>
            {overrideColumns.map(({groupId, paxId, colSpan, border, marginPax, from, to}) => (
                <td key={groupId + paxId} colSpan={colSpan} className={cs("text-right", "pax", {
                    "border-right": border,
                    "margin-pax": marginPax || (from <= marginParticipants && marginParticipants <= to)
                })}>
                    <InputCell
                        choiceId={choiceId}
                        groupId={groupId}
                        paxId={paxId}
                        field={field}
                        isShowUpchargeDelta={isShowUpchargeDelta}
                        baselineChoiceId={baselineChoiceId}
                    />
                </td>
            ))}
        </FooterRow>
    )
}

function InputCell({choiceId, groupId, paxId, field, isShowUpchargeDelta, baselineChoiceId}) {
    const dispatch = useDispatch();
    const {updating} = useChoice(choiceId);
    const currency = useField('currency');

    const get = (o, k) => o?.[k] ?? o?.[k + 'Default'] ?? '';

    const currentRow = useChoicePrice(choiceId, groupId, paxId);
    const baselineRow = baselineChoiceId ? useChoicePrice(baselineChoiceId, groupId, paxId) : null;
    const baselineGroupRow = baselineChoiceId ? useChoicePrice(baselineChoiceId, groupId) : null;

    const useDelta = isShowUpchargeDelta && !!baselineChoiceId && 'retailPrice' === field;

    const basePrice = parseNumber(
        baselineRow?.upchargeGroupBasePrice
        ?? baselineRow?.upchargeGroupBasePriceDefault
        ?? baselineGroupRow?.upchargeGroupBasePrice
        ?? baselineGroupRow?.upchargeGroupBasePriceDefault
    );

    const savedUpcharge = parseNumber(get(currentRow, "upcharge"));
    const currentRetail = parseNumber(get(currentRow, "retailPrice"));

    const calculatedUpcharge = (
        null !== currentRetail && null !== basePrice
            ? currentRetail - basePrice
            : null
    );

    const displayUpcharge = null !== calculatedUpcharge ? calculatedUpcharge : savedUpcharge;

    const inputValue = useDelta && null !== displayUpcharge
        ? String(roundToCurrencyMajor(displayUpcharge, currency))
        : get(currentRow, field);

    function onChange(value) {
        value = 'string' === typeof value ? value.trim() : value;

        if (!useDelta) {
            dispatch(setChoicePriceField(field, choiceId, groupId, paxId, String(value ?? '')));
            return;
        }

        const delta = parseNumber(value);

        if (null === delta || null === basePrice) {
            return;
        }

        const retailPrice = roundToCurrencyMajor(basePrice + delta, currency);

        if (!Number.isFinite(retailPrice)) {
            return;
        }

        dispatch(setChoicePriceField("retailPrice", choiceId, groupId, paxId, String(retailPrice)));
        dispatch(setChoicePriceField("upcharge", choiceId, groupId, paxId, String(delta)));
    }

    return (
        <DebouncedInput
            throttle={2000}
            type="number"
            step="0.01"
            style={{maxWidth: "100%"}}
            onChange={onChange}
            disabled={updating}
            value={inputValue ?? ""}
        />
    );
}

function MarginFractionRows({choiceId}) {
    const {marginFractions} = useChoice(choiceId)

    return marginFractions.map(fraction => (
        <MarginFractionRow key={fraction} choiceId={choiceId} fraction={fraction}/>
    ))
}

function MarginFractionRow({choiceId, fraction}) {
    const title = useMarginFractionTitle(fraction)

    return (
        <DisplayRow
            key={fraction}
            choiceId={choiceId}
            color={"___sum" === fraction}
            title={title}
            percentageSelector={cell => cell.marginFractions?.[fraction]?.[0]}
            amountSelector={cell => cell.marginFractions?.[fraction]?.[1]}
        />
    )
}


function MarginTotalRows({choiceId}) {
    const {marginFractions} = useChoice(choiceId)

    return marginFractions.map(fraction => (
        <MarginTotalRow key={fraction} choiceId={choiceId} fraction={fraction}/>
    ))
}

function MarginTotalRow({choiceId, fraction}) {
    return (
        <DisplayRow
            key={fraction}
            choiceId={choiceId}
            color={"___sum" === fraction}
            title="Gesamtmarge"
            help="tourism-journey-calculation.total-margin-help"
            percentageSelector={cell => cell.marginFractions?.[fraction]?.[0]}
            amountSelector={cell => cell.marginFractions?.[fraction]?.[1]*cell.from ?? 0}
        />
    )
}

function SingleRoomUpcharge({choiceId, basePriceByKey, baselineByKey}) {
    const {columns, shortLabel} = useChoice(choiceId)

    const hasSavedUpcharge = columns.some(({upcharge}) => upcharge)

    if (!hasSavedUpcharge) {
        return null
    }

    const getBaseRetail = (cell) => {
        if (basePriceByKey?.has?.(cell.key)) {
            return basePriceByKey.get(cell.key)
        }

        const baseCell = baselineByKey?.get?.(cell.key)
        const base = Number(baseCell?.overrideRetail ?? baseCell?.retail)

        return isFinite(base) ? base : null
    }

    const getUpcharge = (cell) => {
        if (!hasSavedUpcharge) {
            return cell.upcharge
        }

        const price = Number(cell?.overrideRetail ?? cell?.retail)
        const base = getBaseRetail(cell)

        return (isFinite(price) && isFinite(base)) ? price - base : null
    }

    const hasUpcharge = columns.some(cell => {
        const upcharge = getUpcharge(cell)
        return 'number' === typeof upcharge && isFinite(upcharge)
    });

    if (!hasUpcharge) {
        return null
    }

    return (
        <DisplayRow
            choiceId={choiceId}
            title="Aufpreis"
            amountSelector={getUpcharge}
        />
    )
}

function PreviousPricesRow({choiceId}) {
    const dispatch = useDispatch()
    const {percentageConverter} = useContext(CalculationContext)
    const occupancyCount = useChoiceColumns()
    const hideOccupancyChoices = useHideOccupancyChoices()
    const choicesPrices = useSelector(selectAllChoicesPrices)
    const colSpan = useColSpan()
    const columnKeys = useColumnKeys()
    const marginParticipants = useField("marginParticipants")
    let {previousPrice, retail} = useChoice(choiceId)

    if (!columnKeys.length) {
        return null
    }

    previousPrice = "" === previousPrice ? false : parseFloat(previousPrice)

    return (
        <tr>
            <th className="text-right border-right" colSpan={colSpan}>
                Preis Vorjahr<br/>
                <DebouncedInput
                    throttle={200}
                    type="number"
                    step="0.01"
                    value={previousPrice}
                    onChange={onPreviousPriceChange}
                />
            </th>

            {!hideOccupancyChoices && <td className="border-right" colSpan={occupancyCount}/>}

            {columnKeys.map(({groupId, paxId, border, from, to}) => {
                const possibleIds = [makeId(choiceId, groupId, paxId), makeId(choiceId, groupId)]
                const choicesPrice = choicesPrices.find(({id}) => possibleIds.includes(id))
                const price = choicesPrice?.retailPrice ?? choicesPrice?.retailPriceDefault ?? retail ?? false

                return (
                    <td key={groupId + paxId} className={cs("text-right", "pax", {
                        "border-right": border,
                        "margin-pax": from <= marginParticipants && marginParticipants <= to
                    })}>
                        {!price || !previousPrice ? null :
                            percentageConverter.toView(parseFloat(price)/previousPrice - 1, '+0.0%')}
                    </td>
                )
            })}
        </tr>
    )

    function onPreviousPriceChange(value) {
        dispatch(setChoiceField(choiceId, "previousPrice", value))
    }
}
